import { useEffect } from "react";
import Tabs from "@/components/Tabs";
import { useDenyAuthorization } from "@/hooks/useCapabilities";
import AppLayout from "@/layouts/AppLayout";
import PageMessage from "@/components/PageMessage";
import { applyBackendSource } from "@/lib/backendSource";
import { Path, useNavigate } from "@/router";
import { useLocation } from "react-router-dom";
import { useBirthdayGreetingConfigurationAccess, useConfigurationAccess, useInteractionTopicsAccess } from "./access";
import { shouldSwitchConfigurationToLocalBackend } from "./localBackend";

const GLOBAL_ADMIN_TAB_ITEMS = [
  { label: "Career Consultants", to: "/configuration/career-consultants" },
  { label: "Player Engagement", to: "/configuration/player-engagement" },
  { label: "Recruiters", to: "/configuration/recruiters" }
] as const;

const INTERACTION_TOPICS_TAB_ITEM = { label: "Interaction Topics", to: "/configuration/interaction-topics" } as const;

const BIRTHDAY_GREETING_TAB = {
  label: "Birthday Greetings",
  to: "/configuration/birthday-greetings"
} as const;

function ConfigurationTabs({
  showGlobalTabs,
  showTopicsTab,
  showBirthdayGreetingsTab
}: {
  showGlobalTabs: boolean;
  showTopicsTab: boolean;
  showBirthdayGreetingsTab: boolean;
}) {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const normalizedPath = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const tabItems = [
    ...(showGlobalTabs ? GLOBAL_ADMIN_TAB_ITEMS : []),
    ...(showTopicsTab ? [INTERACTION_TOPICS_TAB_ITEM] : []),
    ...(showBirthdayGreetingsTab ? [BIRTHDAY_GREETING_TAB] : [])
  ];

  return (
    <Tabs
      tabItems={tabItems.map(({ label, to }) => ({
        label,
        onClick: () => navigate(to as Path),
        active: normalizedPath === to
      }))}
    />
  );
}

export default function TabLayout({
  children,
  stickyHeader
}: {
  children: React.ReactNode;
  stickyHeader?: React.ReactNode;
}) {
  const denyAuthorization = useDenyAuthorization();
  const { loaded: configurationLoaded, canView: canViewGlobal } = useConfigurationAccess();
  const { loaded: birthdayAccessLoaded, canView: canViewBirthdayGreetings } = useBirthdayGreetingConfigurationAccess();
  const topicsAccess = useInteractionTopicsAccess();
  const location = useLocation();
  const normalizedPath = location.pathname.endsWith("/") ? location.pathname.slice(0, -1) : location.pathname;
  const isBirthdayGreetingsRoute = normalizedPath === BIRTHDAY_GREETING_TAB.to;
  const isTopicsRoute = normalizedPath === INTERACTION_TOPICS_TAB_ITEM.to;
  const isConfigurationIndexRoute = normalizedPath === "/configuration";
  const loaded = isBirthdayGreetingsRoute
    ? birthdayAccessLoaded
    : isTopicsRoute
      ? topicsAccess.loaded
      : isConfigurationIndexRoute
        ? configurationLoaded && birthdayAccessLoaded && topicsAccess.loaded
        : configurationLoaded;
  // Department admins may only see Interaction Topics; assigned consultants may
  // only see Birthday Greetings. Coverage surfaces stay global-admin-only.
  const canViewCurrentRoute = isBirthdayGreetingsRoute
    ? canViewBirthdayGreetings
    : isTopicsRoute
      ? topicsAccess.canView
      : canViewGlobal || (isConfigurationIndexRoute && (canViewBirthdayGreetings || topicsAccess.canView));
  const switchToLocalBackend = loaded && canViewCurrentRoute && shouldSwitchConfigurationToLocalBackend();

  useEffect(() => {
    if (switchToLocalBackend) {
      applyBackendSource("localhost");
    }
  }, [switchToLocalBackend]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    if (!canViewCurrentRoute) {
      denyAuthorization();
    }
  }, [canViewCurrentRoute, denyAuthorization, loaded]);

  if (switchToLocalBackend) {
    return (
      <PageMessage
        title="Switching to your local API"
        message="Member 360 Configuration isn't on the shared-dev API yet."
      />
    );
  }

  if (!loaded || !canViewCurrentRoute) {
    return null;
  }

  return (
    <AppLayout>
      <div
        className="sticky z-40 bg-sys-brand-surface"
        style={{ top: "var(--app-topbar-height, 77px)" }}
        data-testid="configuration-sticky-header"
      >
        <ConfigurationTabs
          showGlobalTabs={canViewGlobal}
          showTopicsTab={topicsAccess.canView}
          showBirthdayGreetingsTab={canViewBirthdayGreetings}
        />
        {stickyHeader}
      </div>
      <div className="flex flex-col">{children}</div>
    </AppLayout>
  );
}
